import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';
import { Label } from '@antimatterhq/ui/components/label';
import { AddDetectionRuleSchemaValues } from '@/zod/schemas/detectionRules/AddDetectionRule';
import {
  collateActionTooltip,
  collationTooltip,
} from '@/strings/StringsTooltips';
import { FormDropdown, FormError, FormInput } from '@/components/form/form';
import { FormValues } from '@/components/form/values/FormValues';

export const CollationOptionsSection = () => {
  const { values, handleChange, handleBlur, isSubmitting } =
    useFormikContext<AddDetectionRuleSchemaValues>();

  const withinRequired = useMemo(
    () => !!values.collate?.on.filter((q) => !!q).length,
    [values.collate?.on],
  );
  return (
    <>
      <div className="flex items-center gap-2">
        <Text size="lg" bold>
          Collation options
        </Text>
        <InfoTooltip content={collationTooltip} />
      </div>

      <Spacer size="lg" />

      <Label htmlFor="collate.on" id="collate-on-label">
        Collation columns
      </Label>
      <FormValues
        name="collate.on"
        values={values.collate?.on || []}
        placeholder="Enter a column"
        addButtonText="Add a column"
        disabled={isSubmitting}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-labelledby="collate-on-label"
      />
      <FormError name="collate.on" />

      <Spacer size="lg" />

      <Text color="secondary">
        Notables with the same values for all collation columns will be combined
        using the action chosen below
      </Text>

      <Spacer size="lg" />

      <Label
        htmlFor="collate.within"
        id="collate-within-label"
        required={withinRequired}
      >
        Within window
      </Label>
      <FormInput
        type="text"
        name="collate.within"
        value={values.collate?.within}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Enter a time duration, e.g. 10m"
        aria-labelledby="collate-within-label"
      />
      <FormError name="collate.within" />

      <Spacer size="lg" />

      <Label
        info={collateActionTooltip}
        htmlFor="collate.action"
        id="collate-action-label"
      >
        Action
      </Label>
      <FormDropdown
        name="collate.action"
        value={values.collate?.action}
        options={['append', 'updateStats', 'inhibit']}
        placeholder="Select action"
        aria-labelledby="collate-action-label"
      />
      <FormError name="collate.action" />
    </>
  );
};
